import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Loader2, Users, CalendarPlus, ShieldCheck, Search, StickyNote, Save, AlertTriangle, Mail, RefreshCw, Download } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const datum = (iso) => (iso ? new Date(iso).toLocaleDateString("de-DE") : "–");
const PLAN = { einzel: "Einzel", team: "Team", individuell: "Individuell", test: "Testphase" };
const euro = (cent) => (cent == null ? "" : (cent / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" }));

function fehlerText(o) {
  const f = o.zahlung_fehler || {};
  return [f.betrag != null && `${euro(f.betrag)} offen`, f.am && `fehlgeschlagen am ${datum(f.am)}`,
    f.versuche && `${f.versuche}. Versuch`, f.naechster_versuch ? `nächster Versuch ${datum(f.naechster_versuch)}` : o.subscription_status === "unpaid" && "keine weiteren Versuche – nur lesen"]
    .filter(Boolean).join(" · ");
}

function ErinnernKnopf({ o, reload }) {
  const [busy, setBusy] = useState(false);
  const senden = async () => {
    setBusy(true);
    try { const r = await api.post(`/billing/admin/orgs/${o.id}/zahlungs-erinnerung`); toast.success(`Erinnerung an ${r.data.gesendet} Admin${r.data.gesendet === 1 ? "" : "s"} gesendet`); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2 shrink-0">
      {o.zahlung_fehler?.erinnert_am && <span className="text-xs text-red-700/70" data-testid={`abo-zahlung-erinnert-${o.id}`}>zuletzt erinnert {datum(o.zahlung_fehler.erinnert_am)}</span>}
      <Button size="sm" variant="outline" disabled={busy} onClick={senden} className="border-red-300 text-red-700 hover:bg-red-100 bg-white" data-testid={`abo-zahlung-erinnern-${o.id}`}>
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Mail className="h-4 w-4 mr-1" />}Erinnerung senden
      </Button>
    </div>
  );
}

function FehlerListe({ items, onPick, reload }) {
  if (!items.length) return null;
  return (
    <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 p-4" data-testid="abo-zahlung-warnungen">
      <div className="flex items-center gap-2 font-semibold text-red-800"><AlertTriangle className="h-5 w-5" />{items.length} {items.length === 1 ? "Firma hat" : "Firmen haben"} eine fehlgeschlagene Zahlung</div>
      <ul className="mt-3 space-y-2">
        {items.map((o) => (
          <li key={o.id} className="flex flex-col sm:flex-row sm:items-center gap-2 sm:justify-between">
            <button onClick={() => onPick(o)} className="text-left text-sm text-red-900 hover:underline" data-testid={`abo-zahlung-warnung-${o.id}`}>
              <span className="font-semibold">{o.name || o.email}</span> <span className="text-red-700">– {fehlerText(o)}</span>
            </button>
            <ErinnernKnopf o={o} reload={reload} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function badge(o) {
  if (o.exempt) return ["Betreiber", "bg-slate-800 text-white"];
  if (o.zahlung_fehlgeschlagen) return ["Zahlung fehlgeschlagen", "bg-red-600 text-white"];
  if (o.zahlung_offen) return ["Zahlung offen", "bg-red-100 text-red-700"];
  if (o.nur_lesen) return ["Abgelaufen – nur lesen", "bg-red-100 text-red-700"];
  if (o.manuell) return [`${PLAN[o.plan]} (manuell)`, "bg-violet-100 text-violet-700"];
  if (o.plan === "test") return [`Testphase bis ${datum(o.trial_ends_at)}`, "bg-amber-100 text-amber-800"];
  return [`${PLAN[o.plan]} · Stripe`, "bg-green-100 text-green-700"];
}

function OrgNote({ o }) {
  const [note, setNote] = useState(o.note || "");
  const [saved, setSaved] = useState(o.note || "");
  const [open, setOpen] = useState(!!o.note);
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { const r = await api.put(`/billing/admin/orgs/${o.id}/note`, { note }); setSaved(r.data.note); setNote(r.data.note); toast.success("Notiz gespeichert"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  if (!open) {
    return <button onClick={() => setOpen(true)} className="mt-2 inline-flex items-center gap-1 text-xs text-slate-500 hover:text-primary" data-testid={`abo-org-note-add-${o.id}`}><StickyNote className="h-3.5 w-3.5" />Notiz hinzufügen</button>;
  }
  return (
    <div className="mt-3 rounded-xl bg-amber-50/60 border border-amber-200 p-2.5" data-testid={`abo-org-note-${o.id}`}>
      <div className="flex items-center gap-1 text-xs font-semibold text-amber-800 mb-1"><StickyNote className="h-3.5 w-3.5" />Notiz
        {o.note_updated_at && saved === note && <span className="font-normal text-amber-700/70">· zuletzt {datum(o.note_updated_at)}</span>}
      </div>
      <Textarea rows={2} maxLength={2000} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z. B. Sonderpreis 199 € bis 2027, Absprache am Telefon …" className="bg-white text-sm" data-testid={`abo-org-note-input-${o.id}`} />
      {note !== saved && (
        <div className="flex justify-end gap-2 mt-2">
          <Button size="sm" variant="ghost" onClick={() => setNote(saved)} data-testid={`abo-org-note-cancel-${o.id}`}>Verwerfen</Button>
          <Button size="sm" disabled={busy} onClick={save} data-testid={`abo-org-note-save-${o.id}`}>{busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Save className="h-4 w-4 mr-1" />}Speichern</Button>
        </div>
      )}
    </div>
  );
}

function OrgRow({ o, reload }) {
  const [plan, setPlan] = useState(o.manual_plan || "team");
  const [until, setUntil] = useState(o.manuell_bis ? o.manuell_bis.slice(0, 10) : "");
  const [busy, setBusy] = useState("");
  const call = async (key, path, body, msg) => {
    setBusy(key);
    try { const r = await api.post(`/billing/admin/orgs/${o.id}/${path}`, body); toast.success(msg || r.data?.info); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy("");
  };
  const [text, cls] = badge(o);
  return (
    <li id={`abo-org-${o.id}`} className={`py-4 ${o.zahlung_fehlgeschlagen ? "-mx-4 sm:-mx-6 px-4 sm:px-6 bg-red-50/60 border-l-4 border-red-500" : ""}`} data-testid={`abo-org-${o.id}`}>
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-semibold truncate">{o.name}</div>
          <div className="text-xs text-slate-500 truncate">{o.email}</div>
          <div className="text-xs text-slate-400 mt-0.5 flex items-center gap-3">
            <span className="inline-flex items-center gap-1"><Users className="h-3 w-3" />{o.nutzer} Nutzer</span>
            {o.current_period_end && ["active", "trialing", "past_due"].includes(o.subscription_status) && <span>Stripe-Laufzeit bis {datum(o.current_period_end)}</span>}
            {o.subscription_status === "canceled" && <span data-testid={`abo-org-stripe-beendet-${o.id}`}>Stripe-Abo beendet</span>}
            {["rechnung_offen", "unbezahlt"].includes(o.subscription_status) && <span className="text-red-600">Stripe-Rechnung nicht bezahlt</span>}
            {o.manuell && <span>manuell {o.manuell_bis ? `bis ${datum(o.manuell_bis)}` : "unbefristet"}</span>}
          </div>
          {o.zahlung_fehlgeschlagen && <div className="text-xs text-red-700 font-medium mt-1" data-testid={`abo-org-zahlung-fehler-${o.id}`}>{fehlerText(o)}</div>}
        </div>
        <div className="flex items-center gap-2">
          {o.stripe_kunde && (
            <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => call("s", "stripe-abgleich", {}, null)} title="Stand direkt bei Stripe neu abholen" className="h-7 px-2 text-xs" data-testid={`abo-org-stripe-sync-${o.id}`}>
              <RefreshCw className={`h-3.5 w-3.5 mr-1 ${busy === "s" ? "animate-spin" : ""}`} />Mit Stripe abgleichen
            </Button>
          )}
          <span className={`text-xs font-semibold rounded-full px-2.5 py-1 ${cls}`} data-testid={`abo-org-status-${o.id}`}>{text}</span>
        </div>
      </div>
      {!o.exempt && (
        <div className="mt-3 flex flex-col lg:flex-row lg:items-center gap-2">
          <div className="flex flex-wrap items-center gap-2">
            <Select value={plan} onValueChange={setPlan}>
              <SelectTrigger className="w-36 h-9" data-testid={`abo-org-plan-${o.id}`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="einzel">Einzel</SelectItem>
                <SelectItem value="team">Team</SelectItem>
                <SelectItem value="individuell">Individuell</SelectItem>
              </SelectContent>
            </Select>
            <Input type="date" value={until} onChange={(e) => setUntil(e.target.value)} className="w-40 h-9" title="leer = unbefristet" data-testid={`abo-org-until-${o.id}`} />
            <Button size="sm" disabled={!!busy} onClick={() => call("m", "manual", { plan, until: until || null }, "Tarif freigegeben")} data-testid={`abo-org-grant-${o.id}`}>
              {busy === "m" ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <ShieldCheck className="h-4 w-4 mr-1" />}Freigeben
            </Button>
            {o.manual_plan && (
              <Button size="sm" variant="ghost" disabled={!!busy} onClick={() => call("r", "manual", { plan: null }, "Manuelle Freigabe entfernt")} data-testid={`abo-org-revoke-${o.id}`}>Freigabe entfernen</Button>
            )}
          </div>
          <div className="flex items-center gap-2 lg:ml-auto">
            {[14, 30].map((d) => (
              <Button key={d} size="sm" variant="outline" disabled={!!busy} onClick={() => call(`t${d}`, "trial", { days: d }, `Testphase um ${d} Tage verlängert`)} data-testid={`abo-org-trial${d}-${o.id}`}>
                <CalendarPlus className="h-4 w-4 mr-1" />+{d} Tage Test
              </Button>
            ))}
          </div>
        </div>
      )}
      <OrgNote o={o} />
    </li>
  );
}

function AlleAbgleichen({ reload, anzahl }) {
  const [busy, setBusy] = useState(false);
  const los = async () => {
    setBusy(true);
    try { const r = await api.post("/billing/admin/stripe-abgleich-alle"); (r.data.fehler ? toast.warning : toast.success)(r.data.info); reload(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <Button variant="outline" disabled={busy || !anzahl} onClick={los} title={anzahl ? "Alle Firmen mit Stripe-Konto neu abgleichen" : "Keine Firma mit Stripe-Konto"} data-testid="abo-stripe-sync-all-btn">
      <RefreshCw className={`h-4 w-4 mr-2 ${busy ? "animate-spin" : ""}`} />{busy ? "Gleiche ab …" : `Alle mit Stripe abgleichen (${anzahl})`}
    </Button>
  );
}

const MONATE = ["Jan", "Feb", "Mär", "Apr", "Mai", "Jun", "Jul", "Aug", "Sep", "Okt", "Nov", "Dez"];

function Balken({ verlauf }) {
  if (!verlauf?.length) return null;
  const max = Math.max(...verlauf.map((v) => v.betrag), 1);
  const summe = verlauf.reduce((s, v) => s + v.betrag, 0);
  return (
    <div className="mt-3 rounded-xl border border-slate-200 bg-white p-4" data-testid="abo-einnahmen-diagramm">
      <div className="flex justify-between text-xs text-slate-500 mb-3"><span>Letzte 12 Monate</span><span>Summe {euro(summe)}</span></div>
      <div className="flex items-end gap-1 sm:gap-2 h-32">
        {verlauf.map((v, i) => {
          const [m, j] = v.monat.split(".");
          const aktuell = i === verlauf.length - 1;
          return (
            <div key={v.monat} className="flex-1 h-full flex flex-col justify-end items-center group relative" data-testid={`abo-einnahmen-balken-${v.monat}`}>
              <div className={`absolute -top-1 -translate-y-full ${i < 3 ? "left-0" : i > verlauf.length - 4 ? "right-0" : ""} opacity-0 group-hover:opacity-100 transition-opacity bg-slate-800 text-white text-[11px] rounded px-1.5 py-0.5 whitespace-nowrap pointer-events-none z-10`}>{MONATE[+m - 1]} {j}: {euro(v.betrag)}</div>
              <div className={`w-full rounded-t ${aktuell ? "bg-primary" : "bg-primary/40"} transition-[height] duration-500`} style={{ height: `${v.betrag ? Math.max(4, (v.betrag / max) * 100) : 1}%` }} />
              <div className={`mt-1 text-[10px] ${aktuell ? "font-semibold text-slate-700" : "text-slate-400"}`}>{MONATE[+m - 1]}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function CsvExport() {
  const jetzt = new Date();
  const [art, setArt] = useState("monat");
  const [monat, setMonat] = useState(`${jetzt.getFullYear()}-${String(jetzt.getMonth() + 1).padStart(2, "0")}`);
  const [jahr, setJahr] = useState(String(jetzt.getFullYear()));
  const [busy, setBusy] = useState(false);
  const jahre = Array.from({ length: jetzt.getFullYear() - 2025 }, (_, i) => String(jetzt.getFullYear() - i));
  const laden = async () => {
    setBusy(true);
    try {
      const params = art === "monat" ? { zeitraum: "monat", monat } : art === "jahr" ? { zeitraum: "jahr", jahr } : { zeitraum: "alle" };
      const r = await api.get("/billing/admin/einnahmen.csv", { params, responseType: "blob" });
      const name = art === "monat" ? `einnahmen_${monat}.csv` : art === "jahr" ? `einnahmen_${jahr}.csv` : "einnahmen_komplett.csv";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data); a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("CSV heruntergeladen");
    } catch (e) {
      let msg = "Export fehlgeschlagen";
      try { msg = formatApiError(JSON.parse(await e.response.data.text()).detail); } catch (_) {}
      toast.error(msg);
    }
    setBusy(false);
  };
  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border border-slate-200 bg-white p-3" data-testid="abo-csv-export">
      <span className="text-sm font-medium text-slate-700 mr-1">CSV für die Buchhaltung:</span>
      <Select value={art} onValueChange={setArt}>
        <SelectTrigger className="w-32 h-9" data-testid="abo-csv-art"><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value="monat">Monat</SelectItem>
          <SelectItem value="jahr">Jahr</SelectItem>
          <SelectItem value="alle">Komplett</SelectItem>
        </SelectContent>
      </Select>
      {art === "monat" && <Input type="month" value={monat} onChange={(e) => setMonat(e.target.value)} className="w-44 h-9" data-testid="abo-csv-monat" />}
      {art === "jahr" && (
        <Select value={jahr} onValueChange={setJahr}>
          <SelectTrigger className="w-28 h-9" data-testid="abo-csv-jahr"><SelectValue /></SelectTrigger>
          <SelectContent>{jahre.map((j) => <SelectItem key={j} value={j}>{j}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <Button size="sm" disabled={busy || (art === "monat" && !monat)} onClick={laden} data-testid="abo-csv-download-btn">
        {busy ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <Download className="h-4 w-4 mr-1" />}Herunterladen
      </Button>
    </div>
  );
}

function Einnahmen() {
  const [d, setD] = useState(null);
  const [fehler, setFehler] = useState("");
  useEffect(() => {
    api.get("/billing/admin/einnahmen").then((r) => setD(r.data))
      .catch((e) => setFehler(e.response ? formatApiError(e.response.data?.detail) : "Keine Verbindung zum Server"));
  }, []);
  if (fehler) return <p className="mb-6 text-sm text-red-700" data-testid="abo-einnahmen-fehler">Einnahmen konnten nicht geladen werden: {fehler}</p>;
  if (!d) return <div className="mb-6 h-24 rounded-xl border border-slate-200 bg-white animate-pulse" />;
  const kachel = (label, wert, sub, cls, id) => (
    <div className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`abo-einnahmen-${id}`}>
      <div className={`text-2xl font-bold ${cls}`}>{euro(wert)}</div>
      <div className="text-xs text-slate-500">{label}{sub ? ` · ${sub}` : ""}</div>
    </div>
  );
  return (
    <div className="mb-6" data-testid="abo-einnahmen">
      <div className="text-sm font-semibold text-slate-700 mb-2">Einnahmen über Stripe · {d.monat}</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {kachel("Eingegangen diesen Monat", d.eingegangen, `${d.anzahl_eingegangen} ${d.anzahl_eingegangen === 1 ? "Zahlung" : "Zahlungen"}`, "text-green-700", "eingegangen")}
        {kachel("Noch offen", d.offen, `${d.anzahl_offen} ${d.anzahl_offen === 1 ? "Rechnung" : "Rechnungen"}`, d.offen ? "text-amber-700" : "", "offen")}
        {kachel("Erstattet diesen Monat", d.erstattet, "", d.erstattet ? "text-red-700" : "", "erstattet")}
      </div>
      <Balken verlauf={d.verlauf} />
      <CsvExport />
      {d.offene_rechnungen.length > 0 && (
        <ul className="mt-3 rounded-xl border border-slate-200 bg-white divide-y divide-slate-100 text-sm" data-testid="abo-offene-rechnungen">
          {d.offene_rechnungen.map((r) => (
            <li key={r.id} className="px-4 py-2 flex flex-wrap items-center justify-between gap-2">
              <span><span className="font-medium">{r.kunde}</span> <span className="text-slate-500">· {euro(r.betrag)}{r.faellig ? ` · fällig ${datum(r.faellig)}` : ""}</span></span>
              {r.url && <a href={r.url} target="_blank" rel="noreferrer" className="text-primary text-xs font-medium hover:underline" data-testid={`abo-offene-rechnung-link-${r.id}`}>Rechnung ansehen</a>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function AbgleichVerlauf({ verlauf }) {
  const [offen, setOffen] = useState(false);
  if (!verlauf?.length) return null;
  const zaehler = {};
  verlauf.forEach((v) => (v.fehlerliste || []).forEach((f) => { zaehler[f.org_id] = { name: f.name, n: (zaehler[f.org_id]?.n || 0) + 1 }; }));
  const wiederholt = Object.values(zaehler).filter((z) => z.n >= 2).sort((a, b) => b.n - a.n);
  return (
    <div className="mt-3 border-t border-slate-100 pt-3">
      {wiederholt.length > 0 && (
        <div className="mb-2 text-sm text-red-700" data-testid="abo-abgleich-wiederholt">
          <AlertTriangle className="inline h-4 w-4 mr-1 -mt-0.5" />Wiederholt fehlgeschlagen: {wiederholt.map((z) => `${z.name} (${z.n}×)`).join(", ")}
        </div>
      )}
      <button onClick={() => setOffen(!offen)} className="text-sm font-medium text-primary hover:underline" data-testid="abo-abgleich-verlauf-toggle">
        {offen ? "Verlauf ausblenden" : `Verlauf der letzten ${verlauf.length} Nächte anzeigen`}
      </button>
      {offen && (
        <ul className="mt-2 divide-y divide-slate-100 text-sm" data-testid="abo-abgleich-verlauf">
          {verlauf.map((v, i) => (
            <li key={v.am + i} className="py-2" data-testid={`abo-abgleich-verlauf-${i}`}>
              <div className="flex flex-wrap gap-x-2">
                <span className="font-medium w-36 shrink-0">{new Date(v.am).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" })}</span>
                <span className={v.fehler ? "text-red-700" : "text-slate-500"}>{v.info}</span>
              </div>
              {(v.aenderungen || []).map((a) => <div key={a.org_id} className="text-xs text-slate-600">{a.name}: {a.vorher} → {a.nachher}</div>)}
              {(v.fehlerliste || []).map((f) => <div key={f.org_id} className="text-xs text-red-700">{f.name}: {f.grund}</div>)}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function LetzterAbgleich({ info }) {
  if (info === undefined) return null;
  if (!info) return <p className="text-xs text-slate-400 mb-4" data-testid="abo-letzter-abgleich-leer">Automatischer Stripe-Abgleich: läuft jede Nacht um 04:00 Uhr – noch kein Lauf.</p>;
  const zeit = new Date(info.am).toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
  return (
    <div className="mb-6 rounded-xl border border-slate-200 bg-white p-4" data-testid="abo-letzter-abgleich">
      <div className="flex flex-wrap items-center gap-x-2 text-sm">
        <RefreshCw className="h-4 w-4 text-slate-400" />
        <span className="font-semibold">Letzter automatischer Abgleich:</span>
        <span data-testid="abo-letzter-abgleich-zeit">{zeit}</span>
        <span className="text-slate-500">· {info.info}</span>
      </div>
      {info.aenderungen?.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm" data-testid="abo-letzter-abgleich-aenderungen">
          {info.aenderungen.map((a) => (
            <li key={a.org_id} className="text-slate-700"><span className="font-medium">{a.name}</span>: <span className="text-slate-500 line-through">{a.vorher}</span> → <span className="font-medium">{a.nachher}</span></li>
          ))}
        </ul>
      )}
      {info.fehlerliste?.length > 0 && (
        <ul className="mt-2 space-y-1 text-sm text-red-700" data-testid="abo-letzter-abgleich-fehler">
          {info.fehlerliste.map((f) => <li key={f.org_id}>{f.name}: Abgleich fehlgeschlagen ({f.grund})</li>)}
        </ul>
      )}
      <AbgleichVerlauf verlauf={info.verlauf} />
    </div>
  );
}

export default function AdminAbos() {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const [ladeFehler, setLadeFehler] = useState("");
  const [letzter, setLetzter] = useState(undefined);
  const load = useCallback(() => {
    setLadeFehler("");
    api.get("/billing/admin/abgleich-letzter").then((r) => setLetzter(r.data || null)).catch(() => setLetzter(undefined));
    api.get("/billing/admin/orgs")
      .then((r) => setItems(Array.isArray(r.data) ? r.data : []))
      .catch((e) => { setItems([]); setLadeFehler(e.response ? `${formatApiError(e.response.data?.detail)} (Fehler ${e.response.status})` : "Keine Verbindung zum Server"); });
  }, []);
  useEffect(() => { load(); }, [load]);
  const shown = (items || []).filter((o) => `${o.name} ${o.email} ${o.note || ""}`.toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => Number(!!b.zahlung_fehlgeschlagen) - Number(!!a.zahlung_fehlgeschlagen));
  const fehler = (items || []).filter((o) => o.zahlung_fehlgeschlagen);
  const pick = (o) => document.getElementById(`abo-org-${o.id}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
  const count = (f) => (items || []).filter(f).length;
  return (
    <div className="max-w-4xl" data-testid="admin-abos-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-4xl sm:text-5xl font-head font-extrabold tracking-tight flex items-center gap-3"><CreditCard className="h-9 w-9 text-primary" />Abos</h1>
          <p className="text-slate-500 mt-2">Alle Firmen-Konten mit Tarif oder Testphase. Tarife lassen sich hier ohne Stripe freigeben oder upgraden.</p>
        </div>
        {items && <AlleAbgleichen reload={load} anzahl={count((o) => o.stripe_kunde)} />}
      </div>
      {ladeFehler && (
        <div className="mb-6 rounded-xl border border-red-200 bg-red-50 p-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:justify-between" data-testid="abo-lade-fehler">
          <span className="text-sm text-red-800">Die Firmenliste konnte nicht geladen werden: {ladeFehler}</span>
          <Button size="sm" variant="outline" onClick={() => { setItems(null); load(); }} data-testid="abo-neu-laden-btn">Erneut laden</Button>
        </div>
      )}
      {items && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
          {[["Zahlung fehlgeschlagen", fehler.length], ["Testphase", count((o) => o.plan === "test")], ["Bezahlt (Stripe)", count((o) => o.plan && o.plan !== "test" && !o.manuell && !o.exempt)],
            ["Manuell", count((o) => o.manuell)], ["Nur lesen", count((o) => o.nur_lesen)]].map(([l, n]) => (
            <div key={l} className="bg-white rounded-xl border border-slate-200 p-3" data-testid={`abo-kpi-${l}`}><div className="text-2xl font-bold">{n}</div><div className="text-xs text-slate-500">{l}</div></div>
          ))}
        </div>
      )}
      <Einnahmen />
      <LetzterAbgleich info={letzter} />
      <FehlerListe items={fehler} onPick={pick} reload={load} />
      <div className="relative mb-3">
        <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Firma, E-Mail oder Notiz suchen" className="pl-9" data-testid="abo-search-input" />
      </div>
      <div className="bg-white rounded-2xl border border-slate-200 px-4 sm:px-6">
        {items === null ? <div className="py-10 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          : shown.length === 0 ? <p className="py-10 text-center text-slate-500">Keine Konten.</p>
          : <ul className="divide-y divide-slate-100">{shown.map((o) => <OrgRow key={o.id} o={o} reload={load} />)}</ul>}
      </div>
    </div>
  );
}
